'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Badge, type BadgeProps } from '@/ui/badge/Badge'
import { gql } from '@gql-generated/gql'
import { Tag as TagType } from '@gql-generated/graphql'
import React from 'react'

gql(/* GraphQL */ `
  fragment PromotionTagFields on PromotionTag {
    label
    type
  }
`)

const PROMOTION_TAG_TINT_MAP: Partial<Record<TagType, BadgeProps['tint']>> = {
  [TagType.Clearance]: 'error',
  [TagType.ClickAndCollect]: 'error',
  [TagType.CustomPrice]: 'info',
  [TagType.Loyalty]: 'loyalty',
  [TagType.MarketPrice]: 'info',
  [TagType.QuantitySale]: 'error',
  [TagType.Refurbished]: 'success',
  [TagType.RefurbishedGradeA]: 'brand',
  [TagType.RefurbishedGradeB]: 'brand',
  [TagType.RefurbishedGradeC]: 'brand',
  [TagType.Sale]: 'error',
  [TagType.WebSale]: 'error',
  [TagType.Promotional]: 'error',
  [TagType.Volume]: 'info',
  [TagType.TierPricing]: 'info',
  [TagType.CustomerAgreement]: 'info',
  [TagType.Bundle]: 'info',
  [TagType.Seasonal]: 'info',
  [TagType.Damaged]: 'info',
  [TagType.Sample]: 'info',
  [TagType.Manual]: 'info',
  [TagType.PriceMatch]: 'info',
  [TagType.Rebate]: 'info',
  [TagType.Other]: 'info',
}

type ProductTagProps = {
  /** The tag sent */
  tag: TagType

  /** The promotion tag sent */
  label?: string

  /** Additional classes to add to the component */
  className?: string
}

export function ProductTagComponent({ tag, label, className }: ProductTagProps): React.ReactNode {
  const { t } = useTranslation(['productTag'])

  if (tag === TagType.ClickAndCollect) {
    return null
  }

  const content = label || t(`productTag:${tag}`)

  if (!content) {
    return null
  }

  return (
    <Badge
      data-testid="tag"
      className={twMerge(className, 'w-fit')}
      tint={PROMOTION_TAG_TINT_MAP[tag] || 'info'}
      size="sm"
    >
      {content}
    </Badge>
  )
}

export const ProductTag = React.memo(ProductTagComponent)
